import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Plus, Send, Trash2 } from 'lucide-react'
import { getProducts, getVendors } from '../../api/master'
import { getApprovalSettings, savePO, submitPOForApproval } from '../../api/purchase'
import { useToast } from '../../components/Toast.jsx'
import ErrorBanner from '../../components/ErrorBanner'
import FieldError from '../../components/FieldError'
import { BackLink, DocHeader } from '../../components/PageParts'
import { localToday } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import { money, num } from '../sales/salesShared'
import { productLabel } from './purchaseShared'
import { CreateOption } from '../../components/CreateOption'
import { ProductOptions } from './itemFor'

const emptyRow = () => ({ product_id: '', qty: '1', unit_cost: '' })

export default function POForm() {
  const nav = useNavigate()
  const [params] = useSearchParams()
  // From the Materials view of the lists (or "Order materials"): new products start as materials.
  const forMaterials = params.get('for') === 'materials'
  const { notify } = useToast()
  const [vendors, setVendors] = useState([])
  const [products, setProducts] = useState([])
  const [vendorId, setVendorId] = useState(params.get('vendor_id') || '')
  const [poDate, setPoDate] = useState(localToday())
  const [rows, setRows] = useState([emptyRow()])
  const [costs, setCosts] = useState([])
  const [threshold, setThreshold] = useState(0)
  const [saving, setSaving] = useState('')
  const busyRef = useRef(false)
  const [formError, setFormError] = useState('')
  const [errors, setErrors] = useState({ header: {}, lines: {} })

  useEffect(() => {
    getVendors().then((vs) => setVendors(vs || [])).catch(() => {})
    getProducts().then((ps) => setProducts(ps || [])).catch(() => {})
    getApprovalSettings().then((cfg) => setThreshold(parseFloat(cfg?.threshold || cfg?.po_threshold || 0) || 0)).catch(() => setThreshold(0))
  }, [])

  const sortedProducts = useMemo(() => [...products].sort((a, b) => productLabel(a).localeCompare(productLabel(b))), [products])
  const itemsTotal = rows.reduce((s, r) => s + num(r.qty) * num(r.unit_cost), 0)
  const costsTotal = costs.reduce((s, c) => s + num(c.amount), 0)
  const total = itemsTotal + costsTotal
  const needsApproval = threshold > 0 && total >= threshold

  const updateRow = (idx, patch) => setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)))
  const pickProduct = (idx, pid) => {
    const p = products.find((x) => String(x.product_id) === String(pid))
    const usual = num(p?.unit_cost) || num(p?.avg_cost)
    updateRow(idx, { product_id: pid, ...(usual && !num(rows[idx]?.unit_cost) ? { unit_cost: usual.toFixed(2) } : {}) })
  }
  const updateCost = (idx, patch) => setCosts((cs) => cs.map((c, i) => (i === idx ? { ...c, ...patch } : c)))

  const validate = () => {
    const header = {}
    const lines = {}
    if (!vendorId) header.vendor_id = 'Choose the vendor.'
    if (!poDate) header.po_date = 'Enter the order date.'
    const filled = rows.filter((r) => r.product_id || num(r.qty) || num(r.unit_cost))
    if (!filled.length) {
      setErrors({ header, lines })
      setFormError('Add at least one item.')
      return false
    }
    rows.forEach((r, i) => {
      if (!r.product_id && !num(r.unit_cost)) return
      const e = {}
      if (!r.product_id) e.product_id = 'Choose a product.'
      if (!(num(r.qty) > 0)) e.qty = 'Enter how many to order.'
      if (!(num(r.unit_cost) > 0)) e.unit_cost = 'Enter the unit cost.'
      if (Object.keys(e).length) lines[i] = e
    })
    const has = Object.keys(header).length || Object.keys(lines).length
    setErrors({ header, lines })
    setFormError(has ? 'Please fix the highlighted fields.' : '')
    return !has
  }

  const payloadFor = () => {
    const items = rows.filter((r) => r.product_id)
    const extra = costs.filter((c) => c.cost_name?.trim() && num(c.amount) > 0).map((c) => ({ cost_name: c.cost_name.trim(), amount: num(c.amount) }))
    const payload = {
      vendor_id: parseInt(vendorId, 10),
      po_date: poDate,
      lines: items.map((r) => ({ product_id: parseInt(r.product_id, 10), quantity: num(r.qty), unit_cost: num(r.unit_cost) })),
      additional_costs: extra,
      total_amount: total,
    }
    const idem = `po:${vendorId}:${poDate}:${items.map((r) => `${r.product_id}|${r.qty}|${r.unit_cost}`).join(';')}:${extra.map((c) => `${c.cost_name}|${c.amount}`).join(';')}`
    return { payload, idem }
  }

  const applyServerErrors = (e) => {
    if (!e.fields) return
    const hdr = {}
    const lineErrs = {}
    for (const [k, v] of Object.entries(e.fields)) {
      const m = /^lines\[(\d+)\]\.([\w_]+)$/.exec(k)
      if (m) lineErrs[m[1]] = { ...(lineErrs[m[1]] || {}), [m[2]]: v }
      else hdr[k] = v
    }
    setErrors((prev) => ({ header: { ...prev.header, ...hdr }, lines: { ...prev.lines, ...lineErrs } }))
  }

  const save = async (andSubmit) => {
    if (busyRef.current || !validate()) return
    const { payload, idem } = payloadFor()
    busyRef.current = true
    setSaving(andSubmit ? 'submit' : 'save')
    try {
      const res = await savePO(payload, { idempotencyKey: idem })
      const poId = res?.po_id
      const po = docNo('po', res?.po_no) || 'Order'
      if (andSubmit && poId) {
        const sub = await submitPOForApproval(poId, 'Submitted from the order form')
        notify(String(sub?.approval_status || '').toLowerCase() === 'pending' ? `${po} sent for approval` : `${po} saved`)
      } else {
        notify(`${po} saved`)
      }
      nav(poId ? `/purchase/po-detail/${poId}` : '/purchase/po-list')
    } catch (e) {
      setFormError(e.message || 'Could not save the order.')
      applyServerErrors(e)
    } finally {
      setSaving('')
      busyRef.current = false
    }
  }

  return (
    <div className="ci-form-page">
      <DocHeader back={<BackLink to="/purchase/po-list" label="Orders" />} title="New purchase order" />

      {formError && <ErrorBanner message={formError} />}

      <div className="ci-form-meta-card">
        <div className="ci-form-meta-top two">
          <label className="ci-form-field">
            <span>Vendor</span>
            <select value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
              <option value="">Choose a vendor…</option>
              <CreateOption kind="vendor" />
              {[...vendors].sort((a, b) => String(a.name).localeCompare(String(b.name))).map((v) => <option key={v.vendor_id} value={v.vendor_id}>{v.name}</option>)}
            </select>
            <FieldError error={errors.header.vendor_id} />
          </label>
          <label className="ci-form-field">
            <span>Order date</span>
            <input type="date" value={poDate} onChange={(e) => setPoDate(e.target.value)} />
            <FieldError error={errors.header.po_date} />
          </label>
        </div>
      </div>

      <div className="ci-form-lines-card">
        <div className="ci-form-lines-head">
          <div className="ci-form-lines-title">Items</div>
          <span>{rows.filter((r) => r.product_id).length} {rows.filter((r) => r.product_id).length === 1 ? 'item' : 'items'}</span>
        </div>
        <div className="ci-form-table-wrap">
          <table className="ci-form-line-table compact lines-cards">
            <thead>
              <tr>
                <th>Product</th>
                <th className="num col-qty">Qty</th>
                <th className="num col-price">Unit cost</th>
                <th className="num col-amount">Amount</th>
                <th className="col-del" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td data-label="Product">
                    <select className="ci-form-cell-select" value={r.product_id} onChange={(e) => pickProduct(i, e.target.value)} aria-label={`Product for item ${i + 1}`}>
                      <option value="">Choose a product…</option>
                      <CreateOption kind="product" params={forMaterials ? { stock_kind: 'material' } : undefined} />
                      <ProductOptions products={sortedProducts} materialsFirst={forMaterials} />
                    </select>
                    <FieldError error={errors.lines?.[i]?.product_id} />
                  </td>
                  <td data-label="Qty" className="lc-half">
                    <input className="ci-form-cell-input num" value={r.qty} inputMode="decimal" onChange={(e) => updateRow(i, { qty: e.target.value })} aria-label={`Quantity for item ${i + 1}`} />
                    <FieldError error={errors.lines?.[i]?.qty} />
                  </td>
                  <td data-label="Unit cost" className="lc-half">
                    <input className="ci-form-cell-input num" value={r.unit_cost} inputMode="decimal" placeholder="0.00" onChange={(e) => updateRow(i, { unit_cost: e.target.value })} aria-label={`Unit cost for item ${i + 1}`} />
                    <FieldError error={errors.lines?.[i]?.unit_cost} />
                  </td>
                  <td className="ci-form-cell-amount" data-label="Amount">{money(num(r.qty) * num(r.unit_cost))}</td>
                  <td className="ci-form-del-cell">
                    <button type="button" className="ci-form-del-btn" onClick={() => setRows((rs) => (rs.length > 1 ? rs.filter((_, j) => j !== i) : [emptyRow()]))} aria-label={`Remove item ${i + 1}`}><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="ci-form-add-row">
          <button type="button" className="ci-form-add-line" onClick={() => setRows((rs) => [...rs, emptyRow()])}><Plus size={14} />Add item</button>
        </div>
      </div>

      <div className="ci-form-lines-card">
        <div className="ci-form-lines-head">
          <div className="ci-form-lines-title">Extra costs</div>
          <span>Shipping, customs and the like, added to the order</span>
        </div>
        {costs.length > 0 && (
          <div className="ci-form-table-wrap">
            <table className="ci-form-line-table compact">
              <tbody>
                {costs.map((c, i) => (
                  <tr key={i}>
                    <td><input className="ci-form-cell-input" value={c.cost_name} placeholder="e.g. Shipping" onChange={(e) => updateCost(i, { cost_name: e.target.value })} aria-label={`Extra cost ${i + 1} name`} /></td>
                    <td className="col-price"><input className="ci-form-cell-input num" value={c.amount} inputMode="decimal" placeholder="0.00" onChange={(e) => updateCost(i, { amount: e.target.value })} aria-label={`Extra cost ${i + 1} amount`} /></td>
                    <td className="ci-form-del-cell"><button type="button" className="ci-form-del-btn" onClick={() => setCosts((cs) => cs.filter((_, j) => j !== i))} aria-label={`Remove extra cost ${i + 1}`}><Trash2 size={15} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="ci-form-add-row">
          <button type="button" className="ci-form-add-line" onClick={() => setCosts((cs) => [...cs, { cost_name: '', amount: '' }])}><Plus size={14} />Add a cost</button>
        </div>
      </div>

      <div className="ci-form-bottom-row">
        <div className="ci-form-hint">
          {needsApproval && <p>This order is over your approval limit of {money(threshold)}, so an admin will need to approve it before goods can be received.</p>}
        </div>
        <div className="ci-form-totals">
          {costsTotal > 0 && (
            <>
              <div className="ci-form-total-row"><span>Items</span><strong>{money(itemsTotal)}</strong></div>
              <div className="ci-form-total-row"><span>Extra costs</span><strong>{money(costsTotal)}</strong></div>
              <div className="ci-form-total-divider" />
            </>
          )}
          <div className="ci-form-grand-total"><span>Order total</span><strong>{money(total)}</strong></div>
        </div>
      </div>

      <div className="ci-form-action-bar">
        <div className="ci-form-action-left">
          {needsApproval ? (
            <>
              <button type="button" className="ci-btn ci-btn-primary" disabled={!!saving} onClick={() => save(true)}><Send size={14} />{saving === 'submit' ? 'Sending…' : 'Save and send for approval'}</button>
              <button type="button" className="ci-btn ci-btn-ghost" disabled={!!saving} onClick={() => save(false)}>{saving === 'save' ? 'Saving…' : 'Save without sending'}</button>
            </>
          ) : (
            <button type="button" className="ci-btn ci-btn-primary" disabled={!!saving} onClick={() => save(false)}>{saving ? 'Saving…' : 'Save order'}</button>
          )}
          <button type="button" className="ci-btn ci-btn-ghost" onClick={() => nav('/purchase/po-list')}>Cancel</button>
        </div>
      </div>
    </div>
  )
}
